import {ButtonGroup, CloseButton, Separator, Toolbar} from "@heroui/react";

import {formRadiusOptions, radiusOptions} from "../../themes/builder-constants";

import {AccentColorSelector} from "./accent-color-selector";
import {BaseColorSlider} from "./base-color-slider";
import {FontFamilySelector} from "./font-family-selector";
import {GetCSSButton} from "./get-css-button";
import {RadiusPopover} from "./radius-selector";
import {RedoButton} from "./redo-button";
import {ResetButton} from "./reset-button";
import {ShuffleButton} from "./shuffle-button";
import {useThemeBuilder} from "./theme-builder-context";
import {ThemePopover} from "./theme-popover";
import {UndoButton} from "./undo-button";

/**
 * Fixed bottom-of-screen panel for the Theme Builder.
 * Slides up from the bottom when `isOpen` is true via CSS transform.
 * Layout mirrors the reference page.tsx two-group bottom bar.
 */
export function ThemeBuilderPanel() {
  const {isOpen, params, setParam, toggleOpen} = useThemeBuilder();

  return (
    <div
      role="dialog"
      aria-label="Theme Builder"
      aria-modal="false"
      className={[
        "fixed right-0 bottom-0 left-0 z-40",
        "bg-background border-border/60 border-t shadow-xl",
        "transition-transform duration-300 ease-in-out",
        isOpen ? "translate-y-0" : "translate-y-full",
      ].join(" ")}
    >
      {/* Header row with Toolbar */}
      <div className="border-border/40 flex items-center justify-between border-b px-4 py-2">
        <span className="text-foreground text-sm font-semibold">Theme Builder</span>
        <div className="flex items-center gap-4">
          <Toolbar aria-label="Theme builder actions">
            {/* History group: Undo, Redo */}
            <ButtonGroup variant="tertiary">
              <UndoButton />
              <ButtonGroup.Separator />
              <RedoButton />
            </ButtonGroup>

            <Separator />

            {/* Actions group: Reset, Shuffle */}
            <ButtonGroup variant="tertiary">
              <ResetButton />
              <ButtonGroup.Separator />
              <ShuffleButton />
            </ButtonGroup>

            <Separator />

            {/* Code group: Get CSS */}
            <ButtonGroup variant="tertiary">
              <GetCSSButton />
            </ButtonGroup>
          </Toolbar>
          <CloseButton className="size-9" onPress={toggleOpen} aria-label="Close theme builder" />
        </div>
      </div>

      {/* Controls — two groups matching the reference page.tsx bottom bar */}
      <div className="flex items-center justify-center gap-4 px-4 py-6 max-[1200px]:flex-col">
        {/* Group 1: color + font controls */}
        <div className="flex items-center gap-4">
          <AccentColorSelector />
          <BaseColorSlider />
          <FontFamilySelector />
        </div>

        {/* Group 2: radius + theme preset controls */}
        <div className="flex items-center gap-4">
          <RadiusPopover
            label="Radius"
            variable="radius"
            description="Affects the overall UI, like menus and modals"
            options={radiusOptions}
            value={params.radius}
            onChange={(id) => setParam("radius", id)}
          />
          <RadiusPopover
            label="Radius Form"
            variable="formRadius"
            description="Affects form elements, like inputs and selects"
            options={formRadiusOptions}
            value={params.formRadius}
            onChange={(id) => setParam("formRadius", id)}
          />
          <ThemePopover />
        </div>
      </div>
    </div>
  );
}
